'use client';

import { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/contexts/TranslationContext';

const languages = [
  { code: 'ru' as const, name: 'Русский', flag: '🇷🇺' },
  { code: 'en' as const, name: 'English', flag: '🇺🇸' },
  { code: 'es' as const, name: 'Español', flag: '🇪🇸' },
];

export default function LanguageSelector() {
  const { language, changeLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // In production, only show Russian (no selector needed)
  // Use client-side check to prevent hydration mismatch
  if (!isClient || process.env.NODE_ENV === 'production') {
    return null;
  }

  const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <div className="relative">
      <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer"
          aria-label="Select language"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
        <span className="text-lg">{currentLanguage?.flag}</span>
        <span className="text-sm font-medium text-gray-900 hidden sm:block">{currentLanguage?.name}</span>
        <ChevronDownIcon 
          className={`w-4 h-4 text-gray-900 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-48 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50" role="listbox" aria-label="Language options">
          {languages.map((lang) => (
            <button
              type="button"
              key={lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={language === lang.code}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-100 transition-colors duration-200 cursor-pointer ${
                language === lang.code ? 'bg-gray-50' : ''
              }`}
            >
              <span className="text-lg" aria-hidden="true">{lang.flag}</span>
              <span className="text-sm font-medium text-gray-900">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}